import { Chip } from "$components/chip";
import { Table, type TableColumnDefinition } from "$components/table";
import ExpiryCell from "$routes/inventory/_lib/expiry-cell";
import { ProductOriginBadge } from "$routes/inventory/_lib/product-origin-badge";
import { Box } from "@material-ui/core";
import React from "react";
import { useIntl } from "react-intl";
import type { ActionedInventoryMoveRequestOverviewQuery } from "../__generated__/page.gql";

type Data =
  ActionedInventoryMoveRequestOverviewQuery["inventoryMoveRequest"]["skus"]["items"];

function mapFetchedDataToTableData(data: Data = []) {
  return data.map((requestSku) => {
    return {
      header: {
        id: requestSku.id,
        skuName: requestSku.sku.name,
        externalReferences: requestSku.sku.externalReferences,
        quantityRequested: requestSku.quantityRequested,
        quantityFulfilled: requestSku.fulfillments.reduce(
          (total, fulfillment) => total + fulfillment.quantityFulfilled,
          0,
        ),
      },
      children: requestSku.fulfillments.map((fulfillment) => ({
        id: fulfillment.id,
        expiryDate: fulfillment.fulfillingLot.expiryDate,
        quantityFulfilled: fulfillment.quantityFulfilled,
      })),
    };
  });
}

type FullRow = ReturnType<typeof mapFetchedDataToTableData>[0];
type Row = FullRow["header"] | FullRow["children"][0];

interface Props {
  loading: boolean;
  data: Data;
}

const ActionedMoveRequestOverviewTable: FC<Props> = ({ loading, data }) => {
  const { formatMessage } = useIntl();

  const columns: TableColumnDefinition[] = [
    {
      field: "skuName",
      headerName: formatMessage({
        id: "inventory.moveRequest.actionedTable.columnLabel.skuName",
        defaultMessage: "Requested Item",
      }),
      flex: 3,
      renderCell: (value: string, _index, _column, row: Row) => {
        if (!("skuName" in row)) {
          return null;
        }
        return (
          <Box display="flex" alignItems="center" gridGap={8}>
            <span>{value}</span>
            <ProductOriginBadge externalReferences={row.externalReferences} />
          </Box>
        );
      },
    },
    {
      field: "quantitySent",
      headerName: formatMessage({
        id: "inventory.moveRequest.actionedTable.columnLabel.quantitySent",
        defaultMessage: "Quantity Sent",
      }),
      flex: 1,

      renderCell: (_, _index, _column, row: Row) => {
        // Child rows
        if (!("skuName" in row)) {
          return <>{row.quantityFulfilled}</>;
        }

        let color: React.ComponentProps<typeof Chip>["color"] = "brand";
        if (row.quantityFulfilled < row.quantityRequested) {
          color = "pink";
        }

        return (
          <Chip
            label={`${row.quantityFulfilled} / ${row.quantityRequested}`}
            color={color}
          />
        );
      },
    },
    {
      field: "expiryDate",
      headerName: formatMessage({
        id: "inventory.fulfillmoveRequest.table.columnLabel.expiryDate",
        defaultMessage: "Expiry Date",
      }),
      flex: 1,
      renderCell: (expiryDate) =>
        expiryDate ? <ExpiryCell expiryDate={expiryDate} /> : "",
    },
  ];

  return (
    <Table
      loading={loading}
      columns={columns}
      expandingGroups
      rows={mapFetchedDataToTableData(data)}
    />
  );
};

export default ActionedMoveRequestOverviewTable;
